import { FormErrors } from "@mantine/form"

import type { OrganizationRequestDtoCountryEnumKey } from "~/gen/api/types/OrganizationRequestDto.ts"

type SelectField = {
  label: string
  value: string
  description?: string
}

const formFieldTypes = [
  "text",
  "switch",
  "date",
  "select",
  "multiselect",
  "countrySelect",
  "languageSelect",
  "tagsInput",
  "selectListRegistrationId",
  "reference",
  "businessPartnerOrganizationAutocomplete",
  "coordinates",
] as const

export type formFieldType = (typeof formFieldTypes)[number]

/** Address snapshot from BP repository prefill/suggestion; used for per-field lock + API payload. */
export type BusinessPartnerRepositoryAddressSnapshot = {
  readonly id: string
  readonly street: string | null
  readonly city: string | null
  readonly postalCode: string | null
  readonly country: OrganizationRequestDtoCountryEnumKey | null
}

export type BusinessPartnerRepositoryPrefillSelection = {
  readonly name: string
  readonly repositoryRecord: BusinessPartnerRepositoryAddressSnapshot
  /** Present after successful directory prefill: these rows must not be removable (user may still add more). */
  readonly lockedRegistrationIds?: readonly {
    readonly type: string
    readonly registrationId: string
  }[]
}

export interface FormFieldConfig {
  clearable?: boolean
  type: formFieldType
  name: string
  label: string
  placeholder: string
  customClass?: CSSModuleClasses
  options?: SelectField[]
  tooltip?: string
  disabled?: boolean
  readOnly?: boolean
  required?: boolean
  nameType?: string
  span?: number
  order?: number
  defaultChecked?: boolean
  tooltipJustify?: "start" | "end" | "center" | "space-between" | "space-around"
  hidden?: boolean
  onBusinessPartnerRepositorySelected?: (
    selection: BusinessPartnerRepositoryPrefillSelection
  ) => void
  onBusinessPartnerRepositorySelectionCleared?: () => void
  /**
   * First strong directory match id when the user chose Create new (not idle-blur link);
   * `null` when that choice is cleared or irrelevant.
   */
  onDuplicateCandidateForCreateNew?: (
    duplicateCandidateId: string | null
  ) => void
  /** Keys `${type}:${registrationId}` for directory-prefilled rows that cannot be removed. */
  registrationIdRemovalBlockedKeys?: ReadonlySet<string> | null
  /** When set, selecting this organization from BP repository clears `canAnswerOnBehalf`. */
  requesterOrganizationId?: string
  onCheckedChange?: (
    checked: boolean,
    form: {
      setFieldValue: (path: string, value: unknown) => void
      clearFieldError: (path: string) => void
    }
  ) => void
}

export interface FormRowConfig {
  type: "row"
  fields: FormFieldConfig[]
  spacing?: "xs" | "sm" | "md" | "lg" | "xl"
  hidden?: boolean
}

export interface FormGroupConfig {
  type: "group"
  title?: string
  description?: string
  fields: (FormFieldConfig | FormRowConfig)[]
  collapsible?: boolean
  defaultCollapsed?: boolean
  variant?: "default" | "filled" | "outline"
  spacing?: "xs" | "sm" | "md" | "lg" | "xl"
  hidden?: boolean
  isHidden?: (values: Record<string, unknown>) => boolean
  isDisabled?: (values: Record<string, unknown>) => boolean
}

export type FormElement = FormFieldConfig | FormRowConfig | FormGroupConfig

export interface FormConfig<T> {
  validate: (values: Record<string, unknown>) => FormErrors
  initialValues: T
}

export const isFormGroupConfig = (
  element: FormElement
): element is FormGroupConfig => {
  return element.type === "group"
}

export const isFormRowConfig = (
  element: FormElement
): element is FormRowConfig => {
  return element.type === "row"
}

export const isFormFieldConfig = (
  element: FormElement
): element is FormFieldConfig => {
  return formFieldTypes.includes(element.type)
}
